/**
 * A player standing on a platform (spec 02 §3 Walk), ported from rebb64 D_2162 L21EB, D_220C, D_226B
 * (left) and D_22C3 (right).
 */

import { addScore } from '../score';
import { tryBlow } from './blow';
import { startJump } from './jump';
import { solidAt, type Probe } from './probe';
import { JOY_FIRE, JOY_LEFT, JOY_RIGHT, JOY_UP } from './state';
import { GRID_Y, IDLE_ANIM_TICKS, RING_SCORE, WALK_ANIM_TICKS, WALK_STEP } from './tuning';
import type { Player, World } from '../types';

/**
 * L21EB / D_220C: with nothing held and not blowing, the idle animation (the frame's step toggles
 * every 7 ticks). Otherwise UP jumps (and nothing else happens this tick); left, then right, then fire.
 */
export function groundTick(world: World, p: Player, pr: Probe, frame0: number): void {
  const m = p.motion;
  if (m.joy === 0 && m.blow < 0) {
    m.anim = (m.anim + 1) & 0xff;
    if (m.anim === IDLE_ANIM_TICKS) {
      m.frame ^= 1;
      m.anim = 0;
    }
    return;
  }
  if (m.joy & JOY_UP) {
    startJump(p, frame0);
    world.events.push({ type: 'jump', player: p.id });
    return;
  }
  if (m.joy & JOY_LEFT) walk(world, p, pr, frame0, -1);
  if (m.joy & JOY_RIGHT) walk(world, p, pr, frame0, 1);
  if (m.joy & JOY_FIRE) tryBlow(world, p);
}

/**
 * D_226B / D_22C3: a step of 2 px. By the tick-start frame: facing the way held, walk (animating) -
 * or, while blowing that way, walk without animating; facing the other way, turn (the frame only),
 * which costs the tick - but not while blowing. A solid tile beside the lower body blocks the step,
 * tested only when the body is on the column grid and Y is at least $2D (L22A6). The side walls stop
 * the player at X $24 and $F4.
 */
function walk(world: World, p: Player, pr: Probe, frame0: number, dir: 1 | -1): void {
  const m = p.motion;
  const walking = dir < 0 ? frame0 === 4 || frame0 === 5 : frame0 < 2;
  const blowWalking = dir < 0 ? frame0 === 0x0a || frame0 === 0x0b : frame0 === 8 || frame0 === 9;
  if (walking) {
    m.anim = (m.anim + 1) & 0xff;
    if (m.anim >= WALK_ANIM_TICKS) {
      m.anim = 0;
      m.frame ^= 1;
    }
  } else if (!blowWalking) {
    if (m.blow < 0) m.frame = dir < 0 ? 4 : 0;
    return;
  }
  const off = dir < 0 ? (pr.fy !== 0 ? 0x50 : 0x28) : pr.fy !== 0 ? 0x53 : 0x2b;
  if (p.y >= GRID_Y && pr.fx === 0 && solidAt(world, pr, off)) return;
  if (m.redRing) addScore(world, p.id, RING_SCORE);
  p.x = (p.x + dir * WALK_STEP) & 0xff;
  m.walked = true;
}
